<template>
    <div class="dt-toolbar">
        <div class="dt-toolbar-left">
            <template v-if="isCompact">
                <button v-if="!searchOpen && hasSearch" class="dt-toolbar-btn" @click="searchOpen = true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </button>
                <div v-if="searchOpen" class="dt-search dt-search--full">
                    <svg class="dt-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input ref="searchInput" class="dt-search-input" :value="searchValue" :placeholder="searchPlaceholder" @input="onSearchInput"/>
                    <span v-if="loading" class="dt-spinner dt-spinner--sm"></span>
                    <button class="dt-search-close" @click="onSearchClose">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
            </template>
            <template v-else>
                <div v-if="hasSearch" class="dt-search">
                    <svg class="dt-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input class="dt-search-input" :value="searchValue" :placeholder="searchPlaceholder" @input="onSearchInput"/>
                    <span v-if="loading" class="dt-spinner dt-spinner--sm"></span>
                    <button v-if="searchValue && !loading" class="dt-search-clear" @click="onSearchClear">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
            </template>
            <template v-if="!isCompact">
                <div v-for="f in selectFilters" :key="f.key" class="dt-custom-select-wrap">
                    <button class="dt-custom-select" :class="{'dt-custom-select--active': filterValues[f.key], 'dt-custom-select--open': openSelectKey === f.key}" @click.stop="toggleSelectMenu(f.key)">
                        <span class="dt-custom-select-label">{{ filterValues[f.key] ? getOptionLabel(f, filterValues[f.key]) : f.label }}</span>
                        <svg class="dt-custom-select-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                    <div v-if="openSelectKey === f.key" class="dt-custom-select-menu">
                        <button class="dt-custom-select-option" :class="{'dt-custom-select-option--active': !filterValues[f.key]}" @click.stop="onSelectOption(f.key, '')">
                            <svg v-if="!filterValues[f.key]" class="dt-select-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                            <span v-else class="dt-select-check-space"></span>
                            <span>{{ f.label }}</span>
                        </button>
                        <button v-for="opt in f.options" :key="opt.value" class="dt-custom-select-option" :class="{'dt-custom-select-option--active': filterValues[f.key] === opt.value}" @click.stop="onSelectOption(f.key, opt.value)">
                            <svg v-if="filterValues[f.key] === opt.value" class="dt-select-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                            <span v-else class="dt-select-check-space"></span>
                            <span>{{ opt.label }}</span>
                        </button>
                    </div>
                </div>
            </template>
        </div>
        <div class="dt-toolbar-right"></div>
    </div>
</template>

<script>
export default {
    name: 'DataTableToolbar',
    props: {
        filters: Array,
        filterValues: Object,
        isCompact: Boolean,
        loading: Boolean,
    },
    emits: ['filter'],
    data() {
        return {
            searchOpen: false,
            openSelectKey: null,
        }
    },
    created() {
        this.debounceTimer = null
    },
    computed: {
        hasSearch() {
            return this.filters?.some(f => f.type === 'text')
        },
        searchFilter() {
            return this.filters?.find(f => f.type === 'text')
        },
        searchPlaceholder() {
            return this.searchFilter?.placeholder || this.$t('common.dataTable.search')
        },
        searchValue() {
            return this.filterValues[this.searchFilter?.key] || ''
        },
        selectFilters() {
            return (this.filters || []).filter(f => f.type === 'select')
        },
    },
    methods: {
        onSearchInput(e) {
            const key = this.searchFilter?.key
            if (!key) return
            clearTimeout(this.debounceTimer)
            const val = e.target.value
            this.debounceTimer = setTimeout(() => {
                this.$emit('filter', key, val)
            }, 300)
        },
        onSearchClear() {
            const key = this.searchFilter?.key
            if (key) this.$emit('filter', key, '')
        },
        onSearchClose() {
            const key = this.searchFilter?.key
            if (key) this.$emit('filter', key, '')
            this.searchOpen = false
        },
        getOptionLabel(filter, value) {
            const opt = filter.options?.find(o => o.value === value)
            return opt ? opt.label : value
        },
        toggleSelectMenu(key) {
            if (this.openSelectKey === key) {
                this.openSelectKey = null
            } else {
                this.openSelectKey = key
                const close = (e) => {
                    if (!this.$el.querySelector('.dt-custom-select-wrap')?.contains(e.target)) {
                        this.openSelectKey = null
                        document.removeEventListener('mousedown', close)
                    }
                }
                setTimeout(() => document.addEventListener('mousedown', close), 0)
            }
        },
        onSelectOption(key, val) {
            this.$emit('filter', key, val)
            this.openSelectKey = null
        },
    },
    watch: {
        searchOpen(v) {
            if (v) this.$nextTick(() => this.$refs.searchInput?.focus())
        },
    },
}
</script>
